import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  Visibility,
  VisibilityOff,
  Settings,
  Refresh,
  TrendingUp,
  TrendingDown,
  Schedule,
  Stop,
} from '@mui/icons-material';
import { SceneAnalysisData } from '../../types/widget';
import { widgetTheme } from '../../theme/darkTheme';
import { apiService } from '../../services/api';

interface SceneAnalysisWidgetProps {
  widgetId: string;
  title: string;
  data?: SceneAnalysisData[];
  onRefresh?: () => void;
  onConfigure?: () => void;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}

const SceneAnalysisWidget: React.FC<SceneAnalysisWidgetProps> = ({
  widgetId,
  title,
  data = [],
  onRefresh,
  onConfigure,
  isVisible = true,
  onToggleVisibility,
}) => {
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1]));
  const [sceneData, setSceneData] = useState<SceneAnalysisData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [maxDay, setMaxDay] = useState<number>(13);

  // Fetch scene analysis data for the selected day
  const fetchSceneData = async (day: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getSceneAnalysisData(day);
      if (response.success && response.data) {
        setSceneData(response.data);
      } else {
        setError('Failed to fetch scene analysis data');
      }
    } catch (err) {
      setError('Error fetching scene analysis data');
      console.error('Scene analysis error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch production info to get max day
  const fetchProductionInfo = async () => {
    try {
      const response = await apiService.getProductionInfo();
      if (response.success && response.data) {
        setMaxDay(response.data.max_day || 13);
      }
    } catch (error) {
      console.error('Error fetching production info:', error);
      // Default to 13 if API fails
      setMaxDay(13);
    }
  };

  // Load data when selected day changes
  useEffect(() => {
    fetchSceneData(selectedDay);
  }, [selectedDay]);

  // Load production info on component mount
  useEffect(() => {
    fetchProductionInfo();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    fetchSceneData(selectedDay);
    if (onRefresh) {
      onRefresh();
    }
  };

  const currentData = data.length > 0 ? data : [];
  const selectedDayData = sceneData || currentData.find(d => d.day === selectedDay);

  const handleDayChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedDay(newValue);
  };

  const toggleDayExpansion = (day: number) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(day)) {
      newExpanded.delete(day);
    } else {
      newExpanded.add(day);
    }
    setExpandedDays(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ahead': return widgetTheme.colors.status.ahead;
      case 'on_track': return widgetTheme.colors.status.onTrack;
      case 'behind': return widgetTheme.colors.status.behind;
      case 'critical': return widgetTheme.colors.status.critical;
      default: return widgetTheme.colors.status.onTrack;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ahead': return <TrendingUp />;
      case 'behind': return <TrendingDown />;
      case 'critical': return <Stop />;
      default: return <Schedule />;
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: '100%', overflow: 'hidden' }}>
      <CardHeader
        title={title}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure Widget">
              <IconButton onClick={onConfigure} size="small">
                <Settings />
              </IconButton>
            </Tooltip>
            <Tooltip title={isVisible ? "Hide Widget" : "Show Widget"}>
              <IconButton onClick={onToggleVisibility} size="small">
                {isVisible ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      <CardContent sx={{ flexGrow: 1, p: 0, overflow: 'scroll', minHeight: 0 }}>
        {/* Loading and Error States */}
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Box p={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Day Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedDay}
            onChange={handleDayChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 48 }}
          >
            {Array.from({ length: maxDay }, (_, i) => i + 1).map((day) => (
              <Tab
                key={day}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">Day {day}</Typography>
                    {selectedDayData && selectedDayData.day === day && selectedDayData.analysis && (
                      <Chip
                        size="small"
                        label={(selectedDayData.analysis.status || 'on_track').replace('_', ' ')}
                        sx={{
                          backgroundColor: getStatusColor(selectedDayData.analysis.status || 'on_track'),
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                }
                value={day}
              />
            ))}
          </Tabs>
        </Box>

        {/* Selected Day Content */}
        {selectedDayData && selectedDayData.analysis && !loading && (
          <Box sx={{ p: 2 }}>
            {/* Day Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {selectedDayData.analysis.plannedPagesDisplay || selectedDayData.analysis.totalPlannedPages || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pages Planned
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" color="secondary">
                    {selectedDayData.analysis.totalShotPages || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pages Shot
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography 
                    variant="h6" 
                    color={(selectedDayData.analysis.dailyVarianceNumeric || 0) >= 0 ? 'success' : 'error'}
                  >
                    {selectedDayData.analysis.dailyVarianceSign || ''}
                    {selectedDayData.analysis.dailyVariance || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Daily Variance
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    {getStatusIcon(selectedDayData.analysis.status || 'on_track')}
                    <Typography variant="h6" sx={{ color: getStatusColor(selectedDayData.analysis.status || 'on_track') }}>
                      {(selectedDayData.analysis.status || 'on_track').replace('_', ' ').toUpperCase()}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Expandable Sections */}
            <Box>
              {/* Planned Scenes */}
              <Accordion 
                expanded={expandedDays.has(selectedDay)} 
                onChange={() => toggleDayExpansion(selectedDay)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">
                    Planned Scenes ({selectedDayData.plannedScenes?.length || 0})
                  </Typography>
                  <Chip 
                    label={selectedDayData.scheduleVersion || 'Unknown'} 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      maxHeight: 400,
                      overflow: 'scroll',
                      '& .MuiTableHead-root': {
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1
                      }
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Scene</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Pages</TableCell>
                          <TableCell>Characters</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Priority</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedDayData.plannedScenes || []).map((scene) => (
                          <TableRow key={scene.sceneNumber}>
                            <TableCell>{scene.sceneNumber}</TableCell>
                            <TableCell>{scene.description}</TableCell>
                            <TableCell>{scene.pages}</TableCell>
                            <TableCell>{scene.characterCount || 'N/A'}</TableCell>
                            <TableCell>{scene.location}</TableCell>
                            <TableCell>
                              <Chip 
                                label={scene.priorityLevel} 
                                size="small"
                                color={scene.priorityLevel === 1 ? 'error' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* Actual Scenes */}
              <Accordion 
                expanded={expandedDays.has(selectedDay)} 
                onChange={() => toggleDayExpansion(selectedDay)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">
                    Actual Scenes Shot ({selectedDayData.actualScenes?.length || 0})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      maxHeight: 400,
                      overflow: 'scroll',
                      '& .MuiTableHead-root': {
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1
                      }
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Scene</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Pages Shot</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedDayData.actualScenes || []).map((scene) => (
                          <TableRow key={scene.sceneNumber}>
                            <TableCell>{scene.sceneNumber}</TableCell>
                            <TableCell>{scene.description}</TableCell>
                            <TableCell>{scene.pagesShot}</TableCell>
                            <TableCell>
                              {scene.timeStarted && scene.timeCompleted 
                                ? `${scene.timeStarted} - ${scene.timeCompleted}`
                                : 'N/A'
                              }
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={scene.status} 
                                size="small"
                                color={
                                  scene.status === 'completed' ? 'success' :
                                  scene.status === 'partial' ? 'warning' : 'error'
                                }
                              />
                            </TableCell>
                            <TableCell>{scene.notes || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* Analysis */}
              <Accordion 
                expanded={expandedDays.has(selectedDay)} 
                onChange={() => toggleDayExpansion(selectedDay)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">
                    Analysis & Discrepancies
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {/* Intelligent Analysis Summary */}
                    {selectedDayData.analysis.summary && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Production Analysis Summary:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
                          <Typography variant="body1">
                            {selectedDayData.analysis.summary}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* Discrepancy Reasons */}
                    {(selectedDayData.analysis.reasons?.length || 0) > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Reasons for Discrepancies:
                        </Typography>
                        {(selectedDayData.analysis.reasons || []).map((reason, index) => (
                          <Paper key={index} sx={{ p: 2, mb: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                              <Chip label={reason.sceneNumber} size="small" />
                              <Chip 
                                label={reason.category} 
                                size="small" 
                                color="primary"
                              />
                              <Chip 
                                label={reason.impact} 
                                size="small"
                                color={
                                  reason.impact === 'high' ? 'error' :
                                  reason.impact === 'medium' ? 'warning' : 'default'
                                }
                              />
                            </Box>
                            <Typography variant="body2">
                              {reason.reason}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Scenes Not Shot */}
                    {(selectedDayData.analysis.scenesNotShot?.length || 0) > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Scenes Planned but Not Shot:
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(selectedDayData.analysis.scenesNotShot || []).map((scene) => (
                            <Chip key={scene} label={scene} color="error" size="small" />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Scenes Shot Not Planned */}
                    {(selectedDayData.analysis.scenesShotNotPlanned?.length || 0) > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Scenes Shot but Not Planned:
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(selectedDayData.analysis.scenesShotNotPlanned || []).map((scene) => (
                            <Chip key={scene} label={scene} color="warning" size="small" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SceneAnalysisWidget;
